// openEHR RM model — BMM package org.openehr.rm.data_types.text
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { initCodePhrase } from "../../base/init_helpers.ts";
import { parseTerseDvCodedText } from "../../serialization/terse_format.ts";
import type { CodePhraseInit, DvCodedTextInit, DvTextInit } from "../_unassigned.ts";
import { DATA_VALUE } from "./basic.ts";
import type { DV_URI } from "./uri.ts";
import * as openehr_base from "../../base/mod.ts";
import {
  applyRmInit,
  installAssignmentCoercion,
  type RmInit,
} from "../../base/apply_rm_init.ts";

/**
 * DEPRECATED: use markdown formatted \`DV_TEXT\` instead.
 *
 * Original definition:
 *
 * A logical composite text value consisting of a series of \`DV_TEXTs\`, i.e. plain text (optionally coded) potentially with simple formatting, to form a larger tract of prose, which may be interpreted for display purposes as a paragraph.
 *
 * \`DV_PARAGRAPH\` is the standard way for constructing longer text items in summaries, reports and so on.
 */
export class DV_PARAGRAPH extends DATA_VALUE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Items making up the paragraph, each of which is a text item (which may have its own formatting, and/or have hyperlinks).
   */
  items?: undefined;
}

export class DV_TEXT extends DATA_VALUE {
  /**
   * Constructor supports initialization from string or object.
   * 
   * @param init - String (sets value) or initialization object
   * 
   * @example
   * ```typescript
   * // Simple string
   * const text = new DV_TEXT("Hello world");
   * 
   * // Object format
   * const text2 = new DV_TEXT({ value: "Hello", language: "ISO_639-1::en" });
   * ```
   */
  constructor(init?: string | Partial<DvTextInit>) {
    super();
    installAssignmentCoercion(this);
    if (typeof init === 'string') {
      this.value = init;
    } else if (init) {
      if (init.value !== undefined) {
        this.value = init.value;
      }
      if (init.hyperlink !== undefined) {
        this.hyperlink = init.hyperlink as DV_URI;
      }
      if (init.formatting !== undefined) {
        this.formatting = init.formatting;
      }
      if (init.mappings !== undefined) {
        this.mappings = init.mappings as TERM_MAPPING[];
      }
      if (init.language !== undefined) {
        this.language = initCodePhrase(init.language as any);
      }
      if (init.encoding !== undefined) {
        this.encoding = initCodePhrase(init.encoding as any);
      }
    }
  }

  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: openehr_base.String;

  /**
   * Displayable rendition of the item, regardless of its underlying structure. For \`DV_CODED_TEXT\`, this is the rubric of the complete term as provided by the terminology service.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value.
   * Use this to access openehr_base.String methods.
   */
  get $value(): openehr_base.String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or openehr_base.String wrapper.
   */
  set value(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = openehr_base.String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * DEPRECATED: this field is deprecated; use markdown link/text in the \`_value_\` attribute, and \`"markdown"\` as the value of the \`_formatting_\` field.
   *
   * Original usage, prior to RM Release 1.0.4: Optional link sitting behind a section of plain text or coded term item.
   */
  hyperlink?: DV_URI;
  /**
   * Internal storage for formatting
   * @protected
   */
  protected _formatting?: openehr_base.String;

  /**
   * If set, contains one of the following values:
   *
   * * \`"plain"\`: use for plain text, possibly containing newlines, but otherwise unformatted (same as Void);
   * * \`"plain_no_newlines"\`: use for text containing no newlines or other formatting;
   * * \`"markdown"\`: use for markdown formatted text, strongly recommended in the format of the CommonMark specification.
   *
   * DEPRECATED usage: contains a string of the form \`"name:value; name:value..."\` , e.g. \`"font-weight : bold; font-family : Arial; font-size : 12pt;"\`. Values taken from W3C CSS2 properties lists for background and font .
   */
  get formatting(): string | undefined {
    return this._formatting?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for formatting.
   * Use this to access openehr_base.String methods.
   */
  get $formatting(): openehr_base.String | undefined {
    return this._formatting;
  }

  /**
   * Sets formatting from either a primitive value or openehr_base.String wrapper.
   */
  set formatting(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._formatting = undefined;
    } else if (typeof val === "string") {
      this._formatting = openehr_base.String.from(val);
    } else {
      this._formatting = val;
    }
  }

  /**
   * Terms from other terminologies most closely matching this term, typically used where the originator (e.g. pathology lab) of information uses a local terminology but also supplies one or more equivalents from well known terminologies (e.g. LOINC).
   */
  mappings?: TERM_MAPPING[];
  /**
   * Optional indicator of the localised language in which the value is written. Coded from openEHR Code Set  languages . Only used when either the text object is in a different language from the enclosing \`ENTRY\`, or else the text object is being used outside of an \`ENTRY\` or other enclosing structure which indicates the language.
   */
  language?: CODE_PHRASE;
  /**
   * Name of character encoding scheme in which this value is encoded. Coded from openEHR Code Set  character sets . Unicode is the default assumption in openEHR, with UTF-8 being the assumed encoding. This attribute allows for variations from these assumptions.
   */
  encoding?: CODE_PHRASE;
}

export class DV_CODED_TEXT extends DV_TEXT {
  /**
   * Constructor supports initialization from terse format or object.
   * 
   * @param init - String (terse format "terminology::code|value|") or initialization object
   * 
   * @example
   * ```typescript
   * // Terse format
   * const dct = new DV_CODED_TEXT("openehr::433|event|");
   * 
   * // Object format
   * const dct2 = new DV_CODED_TEXT({
   *   value: "event",
   *   defining_code: { code_string: "433", terminology_id: "openehr" }
   * });
   * ```
   */
  constructor(init?: string | Partial<DvCodedTextInit>) {
    if (typeof init === 'string') {
      // Initialize from terse format - parse directly to avoid circular dependency
      const parsed = parseTerseDvCodedText(init);
      if (parsed) {
        super({ value: parsed.value });
        installAssignmentCoercion(this);
        this.defining_code = parsed.defining_code;
      } else {
        super();
        installAssignmentCoercion(this);
      }
    } else {
      super(init);
      installAssignmentCoercion(this);
      if (init && init.defining_code !== undefined) {
        this.defining_code = initCodePhrase(init.defining_code as any);
      }
    }
  }

  /**
   * Internal storage for defining_code
   * @protected
   */
  protected _defining_code?: CODE_PHRASE;

  /**
   * The term of which the  \`_value_\` attribute is the textual rendition (i.e. rubric).
   */
  get defining_code(): CODE_PHRASE | undefined {
    return this._defining_code;
  }

  /**
   * Gets the CODE_PHRASE object for defining_code.
   * Both defining_code and $defining_code return the same CODE_PHRASE instance.
   */
  get $defining_code(): CODE_PHRASE | undefined {
    return this._defining_code;
  }

  /**
   * Sets defining_code from a CODE_PHRASE object.
   */
  set defining_code(val: CODE_PHRASE | undefined) {
    this._defining_code = val;
  }
}

/**
 * Represents a coded term mapped to a \`DV_TEXT\`, and the relative match of the target term with respect to the mapped item. Plain or coded text items may appear in the EHR for which one or mappings in alternative terminologies are required. Mappings are only used to enable computer processing, so they can only be instances of \`DV_CODED_TEXT\`.
 *
 * Used for adding classification terms (e.g. adding ICD classifiers to SNOMED descriptive terms), or mapping into equivalents in other terminologies (e.g. across nursing vocabularies).
 */
export class TERM_MAPPING {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * The relative match of the target term with respect to the mapped text item. Result meanings:
   *
   * * \`'>'\`: the mapping is to a broader term e.g. orginal text =  arbovirus infection , target =  viral infection
   * * \`'='\`: the mapping is to a (supposedly) equivalent to the original item
   * * \`'<'\`: the mapping is to a narrower term. e.g. original text =  diabetes , mapping =  diabetes mellitus .
   * * \`'?'\`: the kind of mapping is unknown.
   *
   * The first three values are taken from the ISO standards 2788 ( Guide to Establishment and development of monolingual thesauri) and 5964 (Guide to Establishment and development of multilingual thesauri).
   */
  match?: string;
  /**
   * Purpose of the mapping e.g. 'automated data mining', 'billing', 'interoperability'.
   */
  purpose?: DV_CODED_TEXT;
  /**
   * The target term of the mapping.
   */
  target?: CODE_PHRASE;
  /**
   * The mapping is to a narrower term.
   * @returns Result value
   */
  narrower(): openehr_base.Boolean {
    // Match code '<' indicates narrower
    return openehr_base.Boolean.from(this.match === "<");
  }

  /**
   * The mapping is to a broader term.
   * @returns Result value
   */
  broader(): openehr_base.Boolean {
    // Match code '>' indicates broader
    return openehr_base.Boolean.from(this.match === ">");
  }

  /**
   * The mapping is to an equivalent term.
   * @returns Result value
   */
  equivalent(): openehr_base.Boolean {
    // Match code '=' indicates equivalent
    return openehr_base.Boolean.from(this.match === "=");
  }

  /**
   * The kind of mapping is unknown.
   * @returns Result value
   */
  unknown(): openehr_base.Boolean {
    // Match code '?' indicates unknown
    return openehr_base.Boolean.from(this.match === "?");
  }

  /**
   * True if match valid.
   * @param c - Parameter
   * @returns Result value
   */
  is_valid_match_code(c: string): openehr_base.Boolean {
    // Valid match codes are: '=' (equivalent), '<' (narrower), '>' (broader), '?' (unknown)
    const validCodes = ["=", "<", ">", "?"];
    return openehr_base.Boolean.from(validCodes.includes(c));
  }
}

export class CODE_PHRASE {
  /**
   * Constructor supports initialization from terse format or object.
   * 
   * @param init - String (terse format "terminology::code") or initialization object
   * 
   * @example
   * ```typescript
   * // Terse format
   * const cp = new CODE_PHRASE("ISO_639-1::en");
   * 
   * // Object format
   * const cp2 = new CODE_PHRASE({
   *   code_string: "en",
   *   terminology_id: "ISO_639-1"
   * });
   * ```
   */
  constructor(init?: string | Partial<CodePhraseInit>) {
    installAssignmentCoercion(this);
    if (typeof init === 'string') {
      // Initialize from terse format
      const parsed = initCodePhrase(init);
      if (parsed) {
        this.terminology_id = parsed.terminology_id;
        this.code_string = parsed.code_string;
        if (parsed.preferred_term) {
          this.preferred_term = parsed.preferred_term;
        }
      }
    } else if (init) {
      // Initialize from object
      if (init.terminology_id !== undefined) {
        if (typeof init.terminology_id === 'string') {
          const termId = new openehr_base.TERMINOLOGY_ID();
          termId.value = init.terminology_id;
          this.terminology_id = termId;
        } else {
          this.terminology_id = init.terminology_id as openehr_base.TERMINOLOGY_ID;
        }
      }
      if (init.code_string !== undefined) {
        this.code_string = init.code_string;
      }
      if (init.preferred_term !== undefined) {
        this.preferred_term = init.preferred_term;
      }
    }
  }

  /**
   * Identifier of the distinct terminology from which the code_string (or its elements) was extracted.
   */
  terminology_id?: openehr_base.TERMINOLOGY_ID;
  /**
   * Internal storage for code_string
   * @protected
   */
  protected _code_string?: openehr_base.String;

  /**
   * The key used by the terminology service to identify a concept or coordination of concepts. This string is most likely parsable inside the terminology service, but nothing can be assumed about its syntax outside that context.
   */
  get code_string(): string | undefined {
    return this._code_string?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for code_string.
   * Use this to access openehr_base.String methods.
   */
  get $code_string(): openehr_base.String | undefined {
    return this._code_string;
  }

  /**
   * Sets code_string from either a primitive value or openehr_base.String wrapper.
   */
  set code_string(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._code_string = undefined;
    } else if (typeof val === "string") {
      this._code_string = openehr_base.String.from(val);
    } else {
      this._code_string = val;
    }
  }

  /**
   * Internal storage for preferred_term
   * @protected
   */
  protected _preferred_term?: openehr_base.String;

  /**
   * Optional attribute to carry preferred term corresponding to the code or expression in \`_code_string_\`. Typical use in integration situations which create mappings, and representing data for which both a (non-preferred) actual term and a preferred term are both required.
   */
  get preferred_term(): string | undefined {
    return this._preferred_term?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for preferred_term.
   * Use this to access openehr_base.String methods.
   */
  get $preferred_term(): openehr_base.String | undefined {
    return this._preferred_term;
  }

  /**
   * Sets preferred_term from either a primitive value or openehr_base.String wrapper.
   */
  set preferred_term(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._preferred_term = undefined;
    } else if (typeof val === "string") {
      this._preferred_term = openehr_base.String.from(val);
    } else {
      this._preferred_term = val;
    }
  }
}
